import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { productLabel } from '~/pages/inventory-import/lib/catalog';
import { MAX_ROWS } from '~/pages/inventory-import/lib/parseFile';
import {
  forgetStockSource,
  rebuildRowsWithEdits,
  resolveRowLength,
  resolveStock,
  type GridRow,
} from '~/pages/inventory-import/lib/rows';
import {
  validateRows,
  type RowReport,
} from '~/pages/inventory-import/lib/validate';
import { lengthDisplay, locationDisplay, productDisplay } from './rowDisplay';

export interface Validation {
  errors: number;
  warnings: number;
  reports: RowReport[];
}

export interface ReviewFilters {
  search: string;
  onlyIssues: boolean;
  focusRowId: number | null;
}

export interface ServerError {
  rowId: number;
  messages: string[];
}

function blankRow(id: number): GridRow {
  return {
    __id: id,
    descriptor: '',
    length: '',
    locationCode: '',
    stock: '',
    leadTimeDays: '',
    replenishmentRate: '',
    moq: '',
    criticalStock: '',
    lowStock: '',
    unit: '',
    product: '',
    location: '',
    lengthInches: '',
    __status: 'none',
    __candidates: [],
    __issues: {},
  };
}

export function useReviewRows(initial: {
  rows: GridRow[];
  res: ReturnType<typeof validateRows>;
}) {
  const [rows, setRows] = useState<GridRow[]>(initial.rows);
  const [validation, setValidation] = useState<Validation>({
    errors: initial.res.errorCount,
    warnings: initial.res.warningCount,
    reports: initial.res.reports,
  });
  const [filters, setFilters] = useState<ReviewFilters>({
    search: '',
    onlyIssues: false,
    focusRowId: null,
  });
  // Per-row failures the backend returns when it rejects the import (all-or-nothing: nothing
  // persists; rowId = GridRow.__id).
  const [serverErrors, setServerErrors] = useState<ServerError[]>([]);

  const rowsRef = useRef<GridRow[]>(rows);
  const visibleRef = useRef<GridRow[]>([]);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const applyRows = useCallback((next: GridRow[]) => {
    const res = validateRows(next);
    setValidation({
      errors: res.errorCount,
      warnings: res.warningCount,
      reports: res.reports,
    });
    setRows(next.slice());
    setServerErrors([]);
  }, []);

  const deleteRow = useCallback(
    (id: number) => applyRows(rowsRef.current.filter((r) => r.__id !== id)),
    [applyRows]
  );
  const addRow = useCallback(() => {
    // Mirror the backend MAX_ROWS cap so the user can't add past it and get a whole-import rejection
    // at submit (the upload path already enforces this; manual additions must too).
    if (rowsRef.current.length >= MAX_ROWS) return;
    const nextId =
      rowsRef.current.reduce((max, r) => Math.max(max, r.__id), 0) + 1;
    applyRows([...rowsRef.current, blankRow(nextId)]);
  }, [applyRows]);
  const pickProduct = useCallback(
    (id: number, productId: string) =>
      applyRows(
        rowsRef.current.map((r) => {
          if (r.__id !== id) return r;
          // Backfill the raw descriptor so it reflects the pick (keeps it non-blank and consistent
          // if the row is later re-resolved); validation reads the resolved product.
          const next = {
            ...r,
            product: productId,
            descriptor: productId ? productLabel(productId) : '',
            __assumedSpecies: '',
            __ignoredWords: '',
          };
          resolveRowLength(next);
          resolveStock(next);
          return next;
        })
      ),
    [applyRows]
  );
  const setField = useCallback(
    (id: number, field: string, value: string) =>
      applyRows(
        rowsRef.current.map((r) => {
          if (r.__id !== id) return r;
          const next = { ...r, [field]: value };
          // A location pick sets the resolved code; keep the raw locationCode in sync so a later
          // re-resolution (resolveRow on a product edit) doesn't revert the pick from a stale value.
          if (field === 'location') next.locationCode = value;
          if (field === 'stock' || field === 'unit') forgetStockSource(next);
          return next;
        })
      ),
    [applyRows]
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RevoGrid edit detail is a loose union
  const handleAfterEdit = (event: CustomEvent<any>) => {
    const detail = event.detail;
    // Collect which props were edited on each row (by __id). RevoGrid has already written the new
    // values onto the source row objects, so we read them off rowsRef.current after cloning.
    const editsByRow = new Map<number, Set<string>>();
    const note = (model: GridRow | undefined, prop: string) => {
      if (!model || typeof prop !== 'string') return;
      const props = editsByRow.get(model.__id) ?? new Set<string>();
      props.add(prop);
      editsByRow.set(model.__id, props);
    };
    if (detail?.model && typeof detail.prop === 'string') {
      note(detail.model as GridRow, detail.prop);
    } else if (detail?.data) {
      // Range edit / paste: detail.data is { [rowIndex]: { [prop]: value } }. Prefer the event's
      // row models (robust to sorting) and fall back to the visible-row index.
      const models = detail.models ?? {};
      for (const idx of Object.keys(detail.data)) {
        const row = (models[idx] as GridRow) ?? visibleRef.current[Number(idx)];
        const changed = detail.data[idx];
        if (!row || !changed) continue;
        for (const prop of Object.keys(changed)) note(row, prop);
      }
    }
    if (editsByRow.size === 0) return;
    applyRows(rebuildRowsWithEdits(rowsRef.current, editsByRow));
  };

  // Defer the filter so typing stays responsive while large files are scanned.
  const deferredSearch = useDeferredValue(filters.search);
  const visibleRows = useMemo(() => {
    // Focusing a single row (from the issues panel) takes precedence over search/filters.
    if (filters.focusRowId != null)
      return rows.filter((r) => r.__id === filters.focusRowId);
    const needle = deferredSearch.trim().toLowerCase();
    let out = rows;
    if (needle) {
      out = out.filter((r) => {
        const haystack = [
          productDisplay(r),
          lengthDisplay(r),
          locationDisplay(r),
          String(r.unit ?? ''),
          String(r.stock ?? ''),
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(needle);
      });
    }
    if (filters.onlyIssues)
      out = out.filter((r) => Object.keys(r.__issues).length > 0);
    return out;
  }, [rows, deferredSearch, filters.onlyIssues, filters.focusRowId]);
  useEffect(() => {
    visibleRef.current = visibleRows;
  }, [visibleRows]);

  return {
    rows,
    validation,
    filters,
    setFilters,
    serverErrors,
    setServerErrors,
    deleteRow,
    addRow,
    pickProduct,
    setField,
    handleAfterEdit,
    visibleRows,
  };
}
